import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, computed, ElementRef, input, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

import * as Hammer from 'hammerjs';
import { Observable, Subject, timer } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { QuestionOption } from '@empathy/survey/base';
import { Question } from '@empathy/survey/base';

@Component({
  selector: 'empathy-carousel-question-view',
  templateUrl: 'carousel-question-view.component.html',
  styleUrls: ['./carousel-question-view.component.scss', './_carousel-question-view-theme.component.scss'],
  animations: [
    trigger('trigState', [
      transition(':enter', [style({ transform: 'translateX(100%)', opacity: 0 }), animate('250ms cubic-bezier(.17,.67,1,1.04)')]),
      transition(':leave', [
        style({ transform: 'translateX(-100%)', opacity: 1, display: 'none' }),
        animate('250ms cubic-bezier(.17,.67,1,1.04)', style({ opacity: 0, display: 'none' }))
      ])
    ])
  ]
})
export class CarouselQuestionViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('carouselSwipeContainer') myBtn: ElementRef;

  question = input.required<Question>();
  questionsGrp = input.required<FormGroup>();

  isPhone$: Observable<boolean>;

  activeCard$ = signal(0);
  movingToNextCard$ = signal(false);
  useMedia$ = computed(() => this.question().visibleOptions.some((option) => !!option.stimulus?.mediaId));
  visibleCards$ = computed(() => this.question().visibleCards);
  visibleOptions$ = computed(() => this.question().visibleOptions);

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;

  otherOption: Partial<QuestionOption> = {
    value: 'OTHER',
    text: 'system.other'
  };

  private destroyed = new Subject<void>();

  private readonly nextCardDelay = 500;
  private phoneBreakpoints = [Breakpoints.XSmall, Breakpoints.Small];

  constructor(breakpointObserver: BreakpointObserver) {
    this.isPhone$ = breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge]).pipe(
      takeUntil(this.destroyed),
      map((result) => {
        for (const queryPoint of Object.keys(result.breakpoints)) {
          if (result.breakpoints[queryPoint]) {
            return this.phoneBreakpoints.includes(queryPoint);
          }
        }

        return false;
      })
    );
  }

  ngAfterViewInit(): void {
    const switcher = this.myBtn.nativeElement;
    const mc = new Hammer(switcher);

    mc.on('swipeleft', () => {
      this.onNext();
    });

    mc.on('swiperight', () => {
      this.onPrev();
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  onMoveToNextCard(cardId: string, change: Partial<QuestionOption>): void {
    if (change.value === this.otherOption.value) {
      this.questionsGrp().get(cardId).get('value').setValue(this.otherOption.value);
      return;
    }

    if (this.questionsGrp().get(cardId).get('otherText').invalid) {
      const cardCtrl = this.questionsGrp().get(cardId);
      const otherTextCtrl = cardCtrl.get('otherText');
      const validators = otherTextCtrl.validator;

      otherTextCtrl.clearValidators();
      otherTextCtrl.reset('', { emitEvent: false });
      otherTextCtrl.setValidators(validators);
      otherTextCtrl.markAsPristine();
      otherTextCtrl.markAsUntouched();
    }

    this.movingToNextCard$.set(true);

    if (!!change.value) {
      this.questionsGrp().get(cardId).get('value').setValue(change.value);
    }

    timer(this.nextCardDelay)
      .pipe(
        tap(() => {
          this.movingToNextCard$.set(false);

          if (this.activeCard$() < this.question().visibleCards.length - 1) {
            this.activeCard$.update((value) => value + 1);
          }
        })
      )
      .subscribe();
  }

  onNext(): void {
    this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).markAllAsTouched();
    if (this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).invalid) {
      return;
    }

    const currentCardAnswered = this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).get('value').value?.length > 0;
    if (this.activeCard$() < this.question().visibleCards.length - 1 && currentCardAnswered) {
      this.activeCard$.update((value) => value + 1);
    }
  }

  onPrev(): void {
    if (this.activeCard$() > 0) {
      this.activeCard$.update((value) => value - 1);
    }
  }
}
